'use client'

import { useEffect } from 'react'

import { UserRoleEnumType } from 'shared/common/global-types.runtime'

import { initClientAnalytics } from './client-instance'
import { useAnalyticsIdentity } from './hooks/use-analytics-identity'
import { useAnalyticsSessionContext } from './hooks/use-analytics-session-context'

interface AnalyticsUser {
  id?: string
  email?: string | null
  role?: UserRoleEnumType | null
  organizationId?: string
}

interface Props {
  user?: AnalyticsUser | null
  appVersion?: string
  workspaceId?: string
  memberId?: string
  isImpersonated?: boolean
  disableLegacyAccess?: boolean
  planViewMvp?: boolean
}

// SDK init lives in a sub-component so its effect fires before the parent's
// effects (React fires child effects first). That ordering matters because
// identify/track calls no-op until the manager is initialized.
const SdkMount = (): React.ReactNode => {
  useEffect(() => {
    initClientAnalytics()
  }, [])
  return null
}

// Wires analytics identity, session context, and the SDK init. Renders nothing.
//
// Page views are intentionally NOT tracked here: Datadog RUM models views as a
// first-class primitive via `nextjsPlugin()`, mounted as <DatadogAppRouter /> /
// <DatadogPagesRouter /> in each app. That's why there is no `pathname` prop.
//
// Gates everything on `user.id` — pass `user={undefined}` (or a user without an
// `id`) and nothing is reported.
const Analytics = ({
  user,
  appVersion,
  workspaceId,
  memberId,
  isImpersonated,
  disableLegacyAccess,
  planViewMvp,
}: Props): React.ReactNode => {
  const isAuthenticated = !!user?.id

  useAnalyticsSessionContext(
    isAuthenticated
      ? {
          workspaceId,
          memberId,
          appVersion,
          userRole: user?.role ?? undefined,
          isImpersonated,
          disableLegacyAccess,
          planViewMvp,
        }
      : undefined,
  )

  useAnalyticsIdentity(
    isAuthenticated
      ? {
          memberId,
          userId: user?.id,
          email: user?.email ?? '',
          role: user?.role,
          organizationId: user?.organizationId,
        }
      : null,
  )

  return isAuthenticated ? <SdkMount /> : null
}

export default Analytics
